import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  SELLER_ENTITY_LABELS_RU,
  type ParsedUnfInvoice,
  type UnfInvoiceApplyResultDto,
  type UnfInvoiceCandidateDto,
  type UnfInvoiceNewLine,
  type UnfInvoicePreviewDto,
} from '@crm/shared'
import {
  applyUnfInvoice,
  deferUnfInvoice,
  previewUnfInvoice,
  type UnfInvoiceLink,
} from '../../../shared/api/orders.api'
import { apiErrorText } from '../../../shared/api/errorText'
import { toast } from '../../../shared/ui/toastStore'
import { readUnfInvoiceFile } from './readUnfInvoiceFile'
import { UnfInvoiceDiff } from './UnfInvoiceDiff'
import { newLineSpec, type LineChoice } from './UnfInvoiceLineChoice'
import { UnfInvoiceMissing } from './UnfInvoiceMissing'
import { UnfInvoiceCandidates, UnfInvoiceVerdict } from './UnfInvoiceVerdict'

const ruDate = (iso: string | null): string => (iso ? iso.split('-').reverse().join('.') : '—')
const rub = (v: number | null): string =>
  v == null ? '—' : v.toLocaleString('ru-RU', { minimumFractionDigits: 2 }) + ' ₽'

/**
 * Загрузка счёта из 1С в заказ (docs/features/unf-invoice-upload.md): выбрать
 * Excel-файл счёта → программа находит похожие заявки, менеджер выбирает нужную, и
 * заказ подстраивается под счёт (21.10.2026): количество и цена — из счёта, строку
 * «нет в заявке» связывают с позицией или заводят новой с квотой, позиции без счёта —
 * «Отказ клиента» или ждут счёта («будут ещё счета»). Счёт можно отложить в «Счета на
 * проверке». С `requestId` (кнопка в карточке заказа) заявку искать не нужно;
 * `initialInvoice` — повторная сверка отложенного счёта без загрузки файла.
 */
export function UnfInvoiceModal({
  requestId,
  initialInvoice,
  onClose,
  onDone,
  onDeferred,
}: {
  requestId?: string
  initialInvoice?: ParsedUnfInvoice
  onClose: () => void
  onDone: (result: UnfInvoiceApplyResultDto) => void
  onDeferred?: () => void
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [invoice, setInvoice] = useState<ParsedUnfInvoice | null>(null)
  const [preview, setPreview] = useState<UnfInvoicePreviewDto | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  /** Сравнение выбранной заявки с учётом связок «та же позиция, что №…». */
  const [linked, setLinked] = useState<UnfInvoiceCandidateDto | null>(null)
  const [choices, setChoices] = useState<Record<number, LineChoice>>({})
  const [moreInvoices, setMoreInvoices] = useState(false)

  const pick = (res: UnfInvoicePreviewDto, id: string | null) => {
    const c = res.candidates.find((x) => x.requestId === id) ?? null
    setSelectedId(id)
    setLinked(null)
    setChoices({})
    setMoreInvoices(c?.moreInvoicesExpected ?? false)
  }

  const check = async (parsed: ParsedUnfInvoice) => {
    setBusy(true)
    setError(null)
    try {
      const res = await previewUnfInvoice(parsed, requestId)
      setInvoice(parsed)
      setPreview(res)
      pick(res, res.fullRequestId ?? res.candidates.find((c) => !c.blocked)?.requestId ?? null)
    } catch (e) {
      setInvoice(null)
      setPreview(null)
      setError(apiErrorText(e, 'Не удалось прочитать счёт'))
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    if (initialInvoice) void check(initialInvoice)
    // Сверка один раз при открытии отложенного счёта.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onFile = async (file: File | undefined) => {
    if (fileRef.current) fileRef.current.value = ''
    if (!file) return
    try {
      await check(await readUnfInvoiceFile(file))
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось прочитать счёт'))
    }
  }

  const candidate = linked ?? preview?.candidates.find((c) => c.requestId === selectedId) ?? null

  const links = (next: Record<number, LineChoice>): UnfInvoiceLink[] =>
    Object.entries(next).flatMap(([no, c]) =>
      c.mode === 'link' ? [{ no: Number(no), itemId: c.itemId }] : [],
    )

  /** Связка строки с позицией меняет сравнение — сверяем выбранную заявку заново. */
  const onChoice = async (no: number, c: LineChoice) => {
    const next = { ...choices, [no]: c }
    setChoices(next)
    const before = choices[no]
    if (!invoice || !selectedId) return
    if (c.mode !== 'link' && before?.mode !== 'link') return
    setBusy(true)
    try {
      const res = await previewUnfInvoice(invoice, selectedId, links(next))
      setLinked(res.candidates[0] ?? null)
    } catch (e) {
      toast.error(apiErrorText(e, 'Не удалось сверить счёт'))
    } finally {
      setBusy(false)
    }
  }

  const newLines: UnfInvoiceNewLine[] = []
  let quotesMissing = 0
  for (const l of candidate?.lines ?? []) {
    // Строка «похоже» (догадка по количеству и цене), которую менеджер переключил на новую позицию.
    const guessedAsNew = l.matchedBy === 'POSITION' && choices[l.no]?.mode === 'new'
    if (l.kind !== 'NEW' && !guessedAsNew) continue
    const spec = newLineSpec(l.no, choices[l.no])
    if (spec) newLines.push(spec)
    else quotesMissing += 1
  }
  const canWrite =
    !!candidate &&
    !candidate.blocked &&
    (!candidate.onlyNumber || candidate.full) &&
    quotesMissing === 0

  const submit = async () => {
    if (!invoice || !candidate || !canWrite) return
    setBusy(true)
    try {
      const res = await applyUnfInvoice(invoice, candidate.requestId, {
        fromOrder: !!requestId,
        moreInvoices,
        links: links(choices),
        newLines,
      })
      const parts = [
        res.changed ? `изменено позиций: ${res.changed}` : '',
        res.added ? `добавлено: ${res.added}` : '',
        res.removed ? `в «Отказ клиента»: ${res.removed}` : '',
        res.waiting ? `ждут счёта: ${res.waiting}` : '',
        res.autoAllocated > 0 ? `разнесено поступлений: ${res.autoAllocated}` : '',
      ].filter(Boolean)
      toast.success(
        `Счёт № ${invoice.number} записан в заказ` +
          (res.orderCreated ? ' — заказ оформлен' : '') +
          (parts.length ? `; ${parts.join(', ')}` : ''),
      )
      onDone(res)
    } catch (e) {
      toast.error(apiErrorText(e, 'Не удалось записать счёт в заказ'))
    } finally {
      setBusy(false)
    }
  }

  const defer = async () => {
    if (!invoice) return
    setBusy(true)
    try {
      await deferUnfInvoice(invoice, requestId)
      toast.success(`Счёт № ${invoice.number} отложен — он в «Счетах на проверке»`)
      onDeferred?.()
      onClose()
    } catch (e) {
      toast.error(apiErrorText(e, 'Не удалось отложить счёт'))
    } finally {
      setBusy(false)
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose()
      }}
    >
      <div
        role="dialog"
        aria-label="Счёт из 1С"
        className="flex max-h-[90vh] w-full max-w-5xl flex-col rounded-xl border border-gray-200 bg-white shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-800">Загрузить счёт из 1С</h3>
          <button
            onClick={onClose}
            disabled={busy}
            aria-label="Закрыть"
            className="text-lg leading-none text-gray-400 hover:text-gray-600 disabled:opacity-40"
          >
            ×
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto p-4">
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.pdf"
              className="hidden"
              aria-label="Файл счёта из 1С"
              onChange={(e) => void onFile(e.target.files?.[0])}
            />
            <button
              type="button"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
              className="px-3 py-1.5 border border-gray-300 bg-white text-sm text-gray-700 rounded-lg hover:border-brand-500 disabled:opacity-60"
            >
              {invoice ? 'Выбрать другой файл' : 'Выбрать файл счёта (Excel или PDF)'}
            </button>
            <span className="text-xs text-gray-500">
              Счёт на оплату, сохранённый из 1С в Excel или PDF. Номера заявки в нём нет — программа
              найдёт её по покупателю и позициям.
            </span>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}

          {invoice && preview && (
            <>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm">
                <div className="font-medium text-gray-900">
                  Счёт № {invoice.number} от {ruDate(invoice.date)} · {rub(invoice.payable)}
                </div>
                <div className="mt-1 text-gray-600">
                  {preview.entityId
                    ? SELLER_ENTITY_LABELS_RU[preview.entityId]
                    : invoice.sellerName}{' '}
                  → {preview.client?.name ?? invoice.buyerName}
                  {invoice.buyerInn ? ` · ИНН ${invoice.buyerInn}` : ''} · позиций:{' '}
                  {invoice.lines.length}
                </div>
              </div>

              {preview.warnings.map((w) => (
                <p key={w} className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                  {w}
                </p>
              ))}

              {!requestId && preview.candidates.length > 1 && (
                <UnfInvoiceCandidates
                  candidates={preview.candidates}
                  selectedId={selectedId}
                  onSelect={(id) => pick(preview, id)}
                />
              )}
              <UnfInvoiceVerdict preview={preview} candidate={candidate} />
              {candidate && !candidate.blocked && (
                <UnfInvoiceDiff
                  candidate={candidate}
                  choices={choices}
                  disabled={busy}
                  onChoice={(no, c) => void onChoice(no, c)}
                />
              )}
              {candidate && !candidate.blocked && !candidate.onlyNumber && (
                <UnfInvoiceMissing
                  candidate={candidate}
                  moreInvoices={moreInvoices}
                  disabled={busy}
                  onChange={setMoreInvoices}
                />
              )}
              {quotesMissing > 0 && (
                <p className="text-sm text-blue-800">
                  Для строк «нет в заявке» выберите позицию заказа или внесите квоту: матрицу,
                  поставщика и закупочную цену.
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
          >
            Отмена
          </button>
          {invoice && (
            <button
              type="button"
              onClick={() => void defer()}
              disabled={busy}
              className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:border-brand-500 disabled:opacity-60"
            >
              Отложить на проверку
            </button>
          )}
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy || !canWrite}
            className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-60"
          >
            {busy ? 'Обрабатываю…' : 'Записать счёт в заказ'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
